import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Player } from '../../services';
import Accordion from '../shared/Accordion';
import Tooltip from '../shared/Tooltip';
import TableScrollTopBar from '../shared/TableScrollTopBar';
import DualCalendarDropdown from '../../../../shared/components/ui/DualCalendarDropdown';
import LoadingSpinner from '../../../../shared/components/ui/LoadingSpinner';
import { playersService } from '../../services';

interface PlayersTableProps {
  searchQuery: string;
  refreshTrigger?: number;
  onEdit?: (entity: Player) => void;
}

interface TableFilters {
  active: 'all' | 'true' | 'false';
  dataSource: 'all' | string;
  position: 'all' | string;
  nationality: 'all' | string;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  updatedStartDate: Date | null;
  updatedEndDate: Date | null;
}

const PlayersTable: React.FC<PlayersTableProps> = ({
  searchQuery,
  refreshTrigger,
  onEdit
}) => {
  const [data, setData] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<TableFilters>({
    active: 'all',
    dataSource: 'all',
    position: 'all',
    nationality: 'all',
    createdStartDate: null,
    createdEndDate: null,
    updatedStartDate: null,
    updatedEndDate: null
  });
  const [activeFilterType, setActiveFilterType] = useState<'created' | 'updated' | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const createdCalendarButtonRef = useRef<HTMLButtonElement>(null);
  const updatedCalendarButtonRef = useRef<HTMLButtonElement>(null);

  const calculateCalendarPosition = (buttonRef: React.RefObject<HTMLButtonElement>) => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCalendarPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.left + window.scrollX
      });
    }
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const players = await playersService.getAll();
      setData(players);
    } catch (err) {
      console.error('Error fetching players:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshTrigger !== undefined) {
      fetchData();
    }
  }, [refreshTrigger, fetchData]);

  const schema = [
    { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
    { name: 'full_name', type: 'varchar', nullable: false, width: 200, minWidth: 150, maxWidth: 250 },
    { name: 'date_of_birth', type: 'date', nullable: true, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'nationality', type: 'varchar', nullable: true, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'profile_image', type: 'varchar', nullable: true, width: 150, minWidth: 100, maxWidth: 200 },
    { name: 'position', type: 'varchar', nullable: true, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'height_cm', type: 'integer', nullable: true, width: 100, minWidth: 80, maxWidth: 120 },
    { name: 'weight_kg', type: 'integer', nullable: true, width: 100, minWidth: 80, maxWidth: 120 },
    { name: 'preferred_foot', type: 'varchar', nullable: true, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'kit_number', type: 'integer', nullable: true, width: 100, minWidth: 80, maxWidth: 120 },
    { name: 'bio', type: 'text', nullable: true, width: 200, minWidth: 150, maxWidth: 250 },
    { name: 'social_media', type: 'json', nullable: true, width: 150, minWidth: 100, maxWidth: 200 },
    { name: 'contact_info', type: 'json', nullable: true, width: 150, minWidth: 100, maxWidth: 200 },
    { name: 'is_active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
    { name: 'data_source', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
    { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
  ];

  const [pagination, setPagination] = useState({
    currentPage: 1,
    recordsPerPage: 100,
    totalRecords: 0,
    totalPages: 0
  });

  useEffect(() => {
    if (data.length > 0) {
      setPagination(prev => ({
        ...prev,
        totalRecords: data.length,
        totalPages: Math.ceil(data.length / prev.recordsPerPage)
      }));
    }
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = data;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.nationality?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.data_source?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Active filter
    if (filters.active !== 'all') {
      filtered = filtered.filter(item => 
        filters.active === 'true' ? item.is_active === true : item.is_active === false
      );
    }

    // Data Source filter
    if (filters.dataSource !== 'all') {
      filtered = filtered.filter(item => item.data_source === filters.dataSource);
    }

    // Position filter
    if (filters.position !== 'all') {
      filtered = filtered.filter(item => item.position === filters.position);
    }

    // Nationality filter
    if (filters.nationality !== 'all') {
      filtered = filtered.filter(item => item.nationality === filters.nationality);
    }

    // Date filters
    if (filters.createdStartDate) {
      filtered = filtered.filter(item => 
        new Date(item.created_at) >= filters.createdStartDate!
      );
    }
    if (filters.createdEndDate) {
      filtered = filtered.filter(item => 
        new Date(item.created_at) <= filters.createdEndDate!
      );
    }
    if (filters.updatedStartDate) {
      filtered = filtered.filter(item => 
        new Date(item.updated_at) >= filters.updatedStartDate!
      );
    }
    if (filters.updatedEndDate) {
      filtered = filtered.filter(item => 
        new Date(item.updated_at) <= filters.updatedEndDate!
      );
    }

    return filtered;
  }, [data, searchQuery, filters]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof Player];
      const bValue = b[sortConfig.key as keyof Player];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Helper function to check if a value is a date string
      const isDateString = (value: any): boolean => {
        if (typeof value !== 'string') return false;
        // Check if it matches common date formats
        return /^\d{4}-\d{2}-\d{2}/.test(value) || // YYYY-MM-DD
               /^\d{2}\/\d{2}\/\d{4}/.test(value) || // DD/MM/YYYY
               /^\d{4}-\d{2}-\d{2}T/.test(value); // ISO format
      };

      // Handle date sorting for any date column
      if (isDateString(aValue) && isDateString(bValue)) {
        const aDate = new Date(aValue as string);
        const bDate = new Date(bValue as string);
        
        // Check if dates are valid
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          return sortConfig.direction === 'asc' 
            ? aDate.getTime() - bDate.getTime() 
            : bDate.getTime() - aDate.getTime();
        }
      }

      // Handle string sorting
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle number sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }

      // Handle boolean sorting
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        return sortConfig.direction === 'asc' 
          ? (aValue === bValue ? 0 : (aValue ? 1 : -1))
          : (aValue === bValue ? 0 : (aValue ? -1 : 1));
      }

      return 0;
    });
  }, [filteredData, sortConfig]);

  const paginatedData = useMemo(() => {
    const startIndex = (pagination.currentPage - 1) * pagination.recordsPerPage;
    return sortedData.slice(startIndex, startIndex + pagination.recordsPerPage);
  }, [sortedData, pagination.currentPage, pagination.recordsPerPage]);

  useEffect(() => {
    if (filteredData.length >= 0) {
      setPagination(prev => ({
        ...prev,
        totalRecords: filteredData.length,
        totalPages: Math.ceil(filteredData.length / prev.recordsPerPage),
        currentPage: 1
      }));
    }
  }, [filteredData]);

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current && current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleRecordsPerPageChange = (recordsPerPage: number) => {
    setPagination(prev => ({
      ...prev,
      recordsPerPage,
      currentPage: 1,
      totalPages: Math.ceil(prev.totalRecords / recordsPerPage)
    }));
  };

  const formatTableData = (value: any, columnName: string): string => {
    if (value === null || value === undefined) return '';
    
    if (columnName === 'created_at' || columnName === 'updated_at' || columnName === 'date_of_birth') {
      return new Date(value).toLocaleDateString();
    }
    
    if (columnName === 'social_media' || columnName === 'contact_info') {
      // Handle JSON fields
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    }
    
    if (columnName === 'is_active') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    return String(value);
  };

  // Get unique values for filter dropdowns
  const uniqueDataSources = useMemo(() => {
    const sources = [...new Set(data.map(item => item.data_source))].filter(Boolean);
    return sources.sort();
  }, [data]);

  const uniquePositions = useMemo(() => {
    const positions = [...new Set(data.map(item => item.position))].filter(Boolean);
    // Only include the 4 standard football positions
    const standardPositions = ['Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
    const allPositions = [...new Set([...positions, ...standardPositions])];
    return allPositions.sort();
  }, [data]);

  const uniqueNationalities = useMemo(() => {
    const nationalities = [...new Set(data.map(item => item.nationality))].filter(Boolean);
    return nationalities.sort();
  }, [data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col space-y-4 overflow-visible">
      {/* Table Schema Accordion */}
      <Accordion
        title="Table Schema"
        defaultOpen={false}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-t border-b border-gray-200">
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Column</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Type</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Nullable</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 border-r border-gray-200 bg-gray-100">Primary Key</th>
                <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 bg-gray-100">Foreign Key</th>
              </tr>
            </thead>
            <tbody>
              {schema.map((column) => (
                <tr key={column.name} className="border-b border-gray-200">
                  <td className="px-3 py-2 text-sm text-gray-900">{column.name}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{column.type}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{column.nullable ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{(column as any).primaryKey ? 'Yes' : 'No'}</td>
                  <td className="px-3 py-2 text-sm text-gray-600">{(column as any).foreignKey || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

      {/* Table Data Accordion */}
      <Accordion
        title="Table Data"
        defaultOpen={true}
      >
        <div className="space-y-4 overflow-visible">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-lg overflow-visible">
            {/* Active Filter */}
            <select
              value={filters.active}
              onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value as 'all' | 'true' | 'false' }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Active: All</option>
              <option value="true">Active: Yes</option>
              <option value="false">Active: No</option>
            </select>

            {/* Data Source Filter */}
            <select
              value={filters.dataSource}
              onChange={(e) => setFilters(prev => ({ ...prev, dataSource: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Data Source: All</option>
              {uniqueDataSources.map(source => (
                <option key={source} value={source}>Data Source: {source}</option>
              ))}
            </select>

            {/* Position Filter */}
            <select
              value={filters.position}
              onChange={(e) => setFilters(prev => ({ ...prev, position: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Position: All</option>
              {uniquePositions.map(position => (
                <option key={position || 'null'} value={position || ''}>Position: {position}</option>
              ))}
            </select>

            {/* Nationality Filter */}
            <select
              value={filters.nationality}
              onChange={(e) => setFilters(prev => ({ ...prev, nationality: e.target.value }))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Nationality: All</option>
              {uniqueNationalities.map(nationality => (
                <option key={nationality || 'null'} value={nationality || ''}>Nationality: {nationality}</option>
              ))}
            </select>

            {/* Date Filters */}
            <div className="flex items-center space-x-2 relative">
              <button
                ref={createdCalendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(createdCalendarButtonRef);
                  setActiveFilterType('created');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.createdStartDate || filters.createdEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Created: {filters.createdStartDate || filters.createdEndDate ? 'Selected' : 'Select'}
              </button>
              
              <button
                ref={updatedCalendarButtonRef}
                onClick={() => {
                  calculateCalendarPosition(updatedCalendarButtonRef);
                  setActiveFilterType('updated');
                  setIsCalendarOpen(!isCalendarOpen);
                }}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  filters.updatedStartDate || filters.updatedEndDate
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Updated: {filters.updatedStartDate || filters.updatedEndDate ? 'Selected' : 'Select'}
              </button>

              {/* Calendar Dropdowns - Rendered via Portal */}
              {isCalendarOpen && createPortal(
                <div 
                  className="fixed z-[9999]"
                  style={{
                    top: `${calendarPosition.top}px`,
                    left: `${calendarPosition.left}px`
                  }}
                >
                  {activeFilterType === 'created' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          createdStartDate: startDate,
                          createdEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={createdCalendarButtonRef}
                    />
                  )}
                  {activeFilterType === 'updated' && (
                    <DualCalendarDropdown
                      isOpen={isCalendarOpen}
                      onClose={() => setIsCalendarOpen(false)}
                      onDateRangeSelect={(startDate, endDate) => {
                        setFilters(prev => ({
                          ...prev,
                          updatedStartDate: startDate,
                          updatedEndDate: endDate
                        }));
                        setIsCalendarOpen(false);
                      }}
                      triggerRef={updatedCalendarButtonRef}
                    />
                  )}
                </div>,
                document.body
              )}
            </div>

            {/* Clear All Filters */}
            <button
              onClick={() => setFilters({
                active: 'all',
                dataSource: 'all',
                position: 'all',
                nationality: 'all',
                createdStartDate: null,
                createdEndDate: null,
                updatedStartDate: null,
                updatedEndDate: null
              })}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              Clear Filters
            </button>
          </div>

          {/* Table */}
          <TableScrollTopBar contentClassName="overflow-y-visible">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10 border-t border-b border-gray-200">
                <tr>
                  {schema.map((column) => (
                    <th
                      key={column.name}
                      className="px-3 py-3 text-left text-sm font-bold text-gray-900 cursor-pointer hover:bg-gray-100 transition-colors border-r border-gray-200 bg-gray-100"
                      onClick={() => handleSort(column.name)}
                      style={{ width: column.width }}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{column.name}</span>
                        {sortConfig?.key === column.name && (
                          <span className="text-blue-500">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-sm font-bold text-gray-900 w-20 bg-gray-100">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={schema.length + 1} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <span className="text-4xl mb-2">📭</span>
                        <span className="text-lg font-medium">No data to show</span>
                        <span className="text-sm text-gray-400 mt-1">Try adjusting your filters or check back later</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item) => (
                    <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                      {schema.map((column) => (
                        <td key={column.name} className="px-3 py-2 text-sm text-gray-900">
                          <Tooltip content={formatTableData(item[column.name as keyof Player], column.name)}>
                            <div className="truncate" style={{ width: column.width }}>
                              {formatTableData(item[column.name as keyof Player], column.name)}
                            </div>
                          </Tooltip>
                        </td>
                      ))}
                      <td className="px-3 py-2 text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onEdit?.(item)}
                            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableScrollTopBar>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Rows per page:</span>
              <select
                value={pagination.recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                disabled={pagination.totalRecords === 0}
                className="px-2 py-1 text-sm border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={100}>100</option>
                <option value={1000}>1000</option>
              </select>
              <span className="text-sm text-gray-700">
                Showing {pagination.totalRecords === 0 ? 0 : (pagination.currentPage - 1) * pagination.recordsPerPage + 1} to{' '}
                {pagination.totalRecords === 0 ? 0 : Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords)} of{' '}
                {pagination.totalRecords} records
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || pagination.totalRecords === 0}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  pagination.currentPage === 1 || pagination.totalRecords === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700">
                Page {pagination.totalRecords === 0 ? 0 : pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages || pagination.totalRecords === 0}
                className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                  pagination.currentPage === pagination.totalPages || pagination.totalRecords === 0
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Accordion>
    </div>
  );
};

export default PlayersTable;
